import {
  Box,
  Image,
  Heading,
  Text,
  Divider,
  Stack,
  Button,
  VStack,
  Link,
  Icon,
  useBreakpointValue,
} from "@chakra-ui/react";
import { MdLocalShipping, MdLock } from "react-icons/md";
import { BuyButton } from "~/components/BuyButton";
import type { Swiper as SwiperType } from "swiper/types";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, FreeMode, Pagination } from "swiper/modules";
import { useEffect, useMemo, useRef, useState } from "react";
import type { FC } from "react";
import { ImageFullScreenButton } from "~/components/ImageFullScreenButton";
import { Link as RouterLink } from "react-router-dom";
import type { IBookPageData } from "../data-store.server.ts";
import _ from "lodash";
import LiteYouTubeEmbed from "react-lite-youtube-embed";
import "react-lite-youtube-embed/dist/LiteYouTubeEmbed.css";

const VariantsLinkList: FC<{ bookInfo: IBookPageData }> = ({ bookInfo }) => {
  const variantsList = bookInfo._join_variants?.map((variant) => {
    return {
      id: variant.id,
      variantName: variant.productInfo.variant,
      slugUrl: variant.slugUrl,
    };
  });
  const variantsDataLinks: {
    id: string;
    variantName: string;
    slugUrl: string;
  }[] = [
    {
      id: bookInfo.id,
      variantName: bookInfo.productInfo.variant,
      slugUrl: bookInfo.slugUrl,
    },
  ].concat(variantsList || []);

  return (
    <Stack spacing={1} my={2} direction={{ base: "row" }}>
      {_.sortBy(variantsDataLinks, (item) => parseInt(item.id, 10)).map(
        (item) => {
          return (
            <VariantLink
              active={item.id === bookInfo.id}
              key={item.id}
              name={item.variantName}
              link={`/ksiazka/${item.slugUrl}`}
            />
          );
        }
      )}
    </Stack>
  );
};
const ImageLinkAttachments = ({
  text,
  src,
  alt,
  url,
}: {
  text?: string;
  src: string;
  alt: string;
  url: string;
}) => {
  return (
    <>
      {text && <Text fontSize={{ base: "sm", md: "md" }}>{text}</Text>}
      <Link as={RouterLink} to={url} isExternal>
        <Image
          p={4}
          bgColor={"white"}
          borderRadius={4}
          my={4}
          src={src}
          alt={alt}
          mx="auto"
          w={{ base: "200px", md: "300px" }}
        />
      </Link>
    </>
  );
};

const YoutubeEmbedAttachemnts = ({
  text,
  videoId,
  params,
}: {
  text?: string;
  videoId: string;
  params?: string;
}) => {
  return (
    <Box width={"100%"}>
      {text && <Text py={4}>{text}</Text>}
      <LiteYouTubeEmbed params={params} id={videoId} title={text || ""} />
    </Box>
  );
};

const Price = ({
  priceInfo,
}: {
  priceInfo: {
    discountPrice?: string;
    regularPrice: string;
  };
}) => {
  if (priceInfo.discountPrice) {
    return (
      <Text my={2} as="h3" fontSize="3xl" color={"primary.500"}>
        <Box
          as="span"
          fontSize={"xl"}
          color={"black"}
          textDecoration={"line-through"}
        >
          {priceInfo.regularPrice}
        </Box>{" "}
        {priceInfo.discountPrice}
      </Text>
    );
  }
  return (
    <Text my={2} as="h3" fontSize="3xl">
      {priceInfo.regularPrice}
    </Text>
  );
};

const VariantLink = ({
  name,
  link,
  active,
}: {
  name: string;
  link: string;
  active: boolean;
}) => {
  return (
    <Button
      p={4}
      size={"sm"}
      variant={"outline"}
      boxSizing="border-box"
      colorScheme={active ? "primary" : "gray"}
      as={RouterLink}
      to={link}
    >
      {name}
    </Button>
  );
};
const ImagesCarusel = ({
  images,
}: {
  images: { src: string; alt: string }[];
}) => {
  const [activeSlideUrl, setActiveSlideUrl] = useState<string | undefined>(
    images[0]?.src
  );
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const enableNavigation = useBreakpointValue(
    {
      base: false,
      md: true,
    },
    {
      fallback: "base",
    }
  );

  useEffect(() => {
    swiper?.slideTo(0);
  }, [images]);

  const slides = images.map((img, index) => {
    return (
      <SwiperSlide key={index}>
        <Image
          mx={"auto"}
          objectFit={"cover"}
          objectPosition={"center"}
          loading={index === 0 ? "eager" : "lazy"}
          fetchPriority={index === 0 ? "high" : "low"}
          src={img.src}
          alt={img.alt}
          // width={'100%'}
          height={"100%"}
        />
      </SwiperSlide>
    );
  });

  return (
    <>
      <Swiper
        style={
          {
            "--swiper-navigation-color": "var(--chakra-colors-primary-500)",
            "--swiper-pagination-color": "var(--chakra-colors-primary-500)",
          } as object
        }
        spaceBetween={0}
        navigation={enableNavigation}
        pagination={{
          dynamicBullets: false,
          bulletClass: "custom-bullet",
          horizontalClass: "custom-horizontal",
        }}
        onSwiper={(_swiper) => {
          setSwiper(_swiper);
        }}
        onSlideChange={(_swiper) => {
          setActiveSlideUrl(images[_swiper.activeIndex]?.src);
        }}
        slidesPerView={1}
        watchSlidesProgress
        modules={[Navigation, FreeMode, Pagination]}
        className="main-swiper"
      >
        {slides}
        <Box as="span" position={"absolute"} top={5} right={5} zIndex={100}>
          {activeSlideUrl && <ImageFullScreenButton url={activeSlideUrl} />}
        </Box>
      </Swiper>
    </>
  );
};

export const BookProductPage = ({ bookInfo }: { bookInfo: IBookPageData }) => {
  const [isBuyButtonFloating, setIsBuyButtonFloating] = useState(false);
  const shouldShowFloatingBuyButton = useBreakpointValue(
    {
      base: true,
      md: false,
    },
    {
      fallback: "base",
    }
  );
  const buyButtonRef = useRef(null);
  const caruselImagesList = useMemo(() => {
    return bookInfo.images.filter((image) => image.type !== "product-cover");
  }, [bookInfo.images]);
  useEffect(() => {
    if (buyButtonRef.current) {
      const observer = new IntersectionObserver(
        ([e]) => {
          setIsBuyButtonFloating(e.intersectionRatio < 1);
        },
        { threshold: [1] }
      );
      observer.observe(buyButtonRef.current);
    }
  }, [buyButtonRef]);

  return (
    <>
      <Stack direction={{ base: "column", md: "row" }}>
        <Box
          border={1}
          p={{ base: 0, md: 4 }}
          mx={{ base: -4, md: 0 }}
          width={{ base: "inherit", md: "50%" }}
          display={"block"}
          height={{ base: "50vh", md: "600px" }}
        >
          <ImagesCarusel images={caruselImagesList} />
        </Box>
        <Box p={{ base: 4 }}>
          <Heading as="h1" size="2xl">
            {bookInfo.productInfo.title}
            <Box fontSize="2xl" fontWeight="normal" lineHeight={1.5}>
              {bookInfo.productInfo.author}
            </Box>
          </Heading>
          {bookInfo.productInfo.translationAuthor && (
            <Text py={1} fontSize={"md"}>
              Przełożył: {bookInfo.productInfo.translationAuthor}{" "}
            </Text>
          )}
          <Price priceInfo={bookInfo.priceInfo} />
          <Box>
            <Text py={1} fontSize={"md"}>
              Wariant:
            </Text>
            <VariantsLinkList bookInfo={bookInfo} />
            <Box ref={buyButtonRef}>
              {shouldShowFloatingBuyButton && isBuyButtonFloating && (
                <Box
                  width={"100%"}
                  position={"fixed"}
                  bottom={0}
                  left={0}
                  p={4}
                  zIndex={10}
                  boxShadow={"0px 5px 30px #888"}
                  bgColor={"white"}
                >
                  <BuyButton productBuyPath={bookInfo.buyLink} />
                </Box>
              )}
              <BuyButton productBuyPath={bookInfo.buyLink} />
            </Box>
            <Box as="div" mt={4}>
              <Text fontSize="xs" fontWeight={"bold"}>
                <Icon
                  as={MdLock}
                  boxSize={4}
                  verticalAlign={"middle"}
                  w={8}
                  color={"secondary"}
                />
                Bezpieczna płatność dzięki szyfrowaniu SSL
              </Text>
              <Text fontSize="xs" verticalAlign={"center"} fontWeight={"bold"}>
                <Icon
                  as={MdLocalShipping}
                  boxSize={4}
                  verticalAlign={"middle"}
                  w={8}
                  color={"secondary"}
                />
                Wysyłka w 1 dzień roboczy
              </Text>
            </Box>
          </Box>
          <Divider my={4} />
          <Box as="div" my={4}>
            {bookInfo._join_variants &&
              bookInfo._join_variants.map((variant) => {
                if (variant.priceInfo.discountPrice) {
                  return (
                    <Text
                      key={variant.id}
                      fontSize={"md"}
                      color={"primary.500"}
                      fontWeight={"bold"}
                    >
                      Rabat&nbsp;
                      {Math.round(
                        100 -
                          (parseFloat(variant.priceInfo.discountPrice) /
                            parseFloat(variant.priceInfo.regularPrice)) *
                            100
                      )}
                      % na wariant {variant.productInfo.variant}
                    </Text>
                  );
                }
              })}
            <Text fontSize={"md"}>
              ISBN: <b>{bookInfo.productInfo.ISBN}</b>
            </Text>
            <Text fontSize={"md"}>
              Wydanie: <b>{bookInfo.productInfo.issueNumber}</b>{" "}
            </Text>
            <Text fontSize={"md"}>
              Premiera:{" "}
              <b>
                {new Date(bookInfo.productInfo.releaseDate).toLocaleDateString(
                  "pl-PL"
                )}
              </b>
            </Text>
            {bookInfo.productInfo.cover && (
              <Text fontSize={"md"}>
                Okładka: <b>{bookInfo.productInfo.cover}</b>
              </Text>
            )}
            {bookInfo.productInfo.format && (
              <Text fontSize={"md"}>
                Format:{" "}
                <b>
                  {bookInfo.productInfo.format
                    .map((f) => {
                      if (typeof f === "object") {
                        return `${f.x}x${f.y}${f.unit}`;
                      }
                      return f;
                    })
                    .join(",")}
                </b>
              </Text>
            )}
            {bookInfo.productInfo.formatInfo && (
              <Text fontSize={"sm"}>({bookInfo.productInfo.formatInfo})</Text>
            )}
            {bookInfo.productInfo.pagesCount && (
              <Text fontSize={"md"}>
                Liczba stron: <b>{bookInfo.productInfo.pagesCount}</b>
              </Text>
            )}
          </Box>
        </Box>
      </Stack>
      <Divider my={8} />
      <Box mt={8}>
        <Heading as={"h3"} size={"lg"}>
          Opis
        </Heading>
        <Stack direction={{ base: "column", md: "row" }} spacing={16}>
          <Box flexBasis={{ base: "100%", md: "60%" }}>
            {bookInfo.description.paragraphs.map(({ text }, index) => {
              return (
                <div key={index}>
                  <Text
                    py={4}
                    dangerouslySetInnerHTML={{ __html: text }}
                  ></Text>
                  {index === 2 && <Divider my={4} />}
                </div>
              );
            })}
          </Box>
          <Box flexBasis={{ base: "100%", md: "40%" }}>
            <VStack spacing={6} py={4} divider={<Divider />}>
              {bookInfo.description.attachments.map((attachment, index) => {
                if (attachment.type === "image-link") {
                  return (
                    <ImageLinkAttachments
                      key={index}
                      text={attachment.text}
                      src={attachment.src}
                      alt={attachment.alt}
                      url={attachment.url}
                    />
                  );
                }
                if (attachment.type === "youtube-embed") {
                  return (
                    <YoutubeEmbedAttachemnts
                      key={index}
                      text={attachment.text}
                      videoId={attachment.videoId}
                      params={attachment.params}
                    />
                  );
                }

                if (attachment.type === "text") {
                  return (
                    <Text
                      key={index}
                      dangerouslySetInnerHTML={{ __html: attachment.text }}
                    />
                  );
                }
              })}
            </VStack>
          </Box>
        </Stack>
      </Box>
    </>
  );
};
